import { useState, useMemo, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import MonacoEditor from "@monaco-editor/react";
import { showToastMessage } from "../../../../../../../utils/commonFunctions";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { DividerLine } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { ThermalModalHeader, ThermalModalFooter } from "../components/common";
import { useThermalConfigSaver, useNodeSpecsModal, validateSelectedNode, showInvalidFieldToasts, validateConductanceMatrix, showConductanceErrors, useModalAutoClose, useJsonModalState } from "../hooks/ThermalDesignerHooks";
import ConfirmationDialog from "../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import { exportNodeSpecsAsJson, readFileAsText } from "../utils/thermalConfigUtils";

const NodeSpecsJsonModal = () => {
  const { mission_id, sat_id } = useParams();
  const { setOpenModal, isSatDesignerSidebarOpen, nodeSpecsOptions, setNodeSpecsOptions } = useThermalDesign();
  const { saveThermalConfig } = useThermalConfigSaver();
  const { isLastAvailableNode, handleSaveAndNext, restoreSidebar } = useNodeSpecsModal();
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const [showAllNodes, setShowAllNodes] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  useModalAutoClose("isNodeSpecsJsonModalOpen");

  const selectedNode = useMemo(() => nodeSpecsOptions.find((node) => node.isSelected), [nodeSpecsOptions]);
  const isLastNode = showAllNodes || isLastAvailableNode();

  const nodeJson = useMemo(() => {
    if (showAllNodes) {
      if (!nodeSpecsOptions || nodeSpecsOptions.length === 0) return "[]";
      return JSON.stringify(nodeSpecsOptions, null, 2);
    } else {
      if (!selectedNode) return "{}";
      return JSON.stringify(selectedNode, null, 2);
    }
  }, [nodeSpecsOptions, selectedNode, showAllNodes]);

  const { json: jsonInput, setJson: setJsonInput, handleJsonChange, handleJsonBlur, reconstructJsonWithOriginalKeys, pendingJsonRef, hasEditedRef } = useJsonModalState(nodeJson);

  useEffect(() => {
    setJsonInput(nodeJson);
    pendingJsonRef.current = null;
  }, [nodeJson, setJsonInput, pendingJsonRef]);

  useEffect(() => {
    if (!hasEditedRef.current) return;
    try {
      const parsedJson = JSON.parse(jsonInput);
      let finalJson = parsedJson;
      if (reconstructJsonWithOriginalKeys && nodeJson) {
        finalJson = reconstructJsonWithOriginalKeys(parsedJson, nodeJson);
      }
      if (showAllNodes && Array.isArray(finalJson)) {
        setNodeSpecsOptions(finalJson);
      } else if (!showAllNodes && typeof finalJson === "object" && selectedNode) {
        setNodeSpecsOptions((prev) =>
          prev.map((node) =>
            node.id === selectedNode.id ? finalJson : node
          )
        );
      }
    } catch {
      // JSON parsing error - don't update state
    }
  }, [jsonInput, hasEditedRef]);

  const handleSaveAndExit = () => {
    if (showAllNodes) {
      let hasInvalid = false;
      const validNodeIds = new Set<string>();
      nodeSpecsOptions.forEach((node) => {
        if (node.status === "unavailable") return;
        const invalid = validateSelectedNode(node);
        if (invalid.size > 0) {
          showInvalidFieldToasts(invalid, node.name);
          hasInvalid = true;
        } else {
          validNodeIds.add(node.id);
        }
      });

      setNodeSpecsOptions((prev) =>
        prev.map((node) =>
          validNodeIds.has(node.id) ? { ...node, status: "complete" as const } : node
        )
      );

      if (hasInvalid) return;

      const matrixNodes = nodeSpecsOptions.filter((n) => n.status !== "unavailable");
      if (matrixNodes.length > 0) {
        const nodeLabels = matrixNodes.map((n) => n.id);
        const matrix = matrixNodes.map((n) => n.values?.node_conductance ?? []);
        const conductanceErrors = validateConductanceMatrix(nodeLabels, matrix);
        if (conductanceErrors.length > 0) {
          showConductanceErrors(conductanceErrors);
          return;
        }
      }
    } else {
      if (!selectedNode) return;
      const invalid = validateSelectedNode(selectedNode);
      if (invalid.size > 0) {
        showInvalidFieldToasts(invalid, selectedNode.name);
        return;
      }
      setNodeSpecsOptions((prev) =>
        prev.map((node) =>
          node.id === selectedNode.id ? { ...node, status: "complete" as const } : node
        )
      );
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    try {
      if (mission_id && sat_id) {
        await saveThermalConfig(mission_id, sat_id);
        restoreSidebar();
        setOpenModal((prev) => ({
          ...prev,
          isNodeSpecsJsonModalOpen: false,
        }));
      }
    } catch {
      showToastMessage("error", "Failed to save thermal configuration");
    } finally {
      setShowConfirmDialog(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    restoreSidebar();
    setOpenModal((prev) => ({
      ...prev,
      isNodeSpecsJsonModalOpen: false,
    }));
  };

  const handleExportJson = () => {
    exportNodeSpecsAsJson(nodeSpecsOptions);
  };

  const handleImport = async (file: File) => {
    try {
      const text = await readFileAsText(file);
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        setNodeSpecsOptions(parsed);
        setJsonInput(JSON.stringify(parsed, null, 2));
        showToastMessage("success", "Node specs imported successfully");
      } else {
        showToastMessage("error", "Import failed: expected an array of nodes");
      }
    } catch {
      showToastMessage("error", "Import failed: could not parse file");
    }
  };

  const header = {
    title: "Node Specifications JSON",
    isModalMinimized,
    setIsModalMinimized,
    setOpenModal,
    modalItem: "isNodeSpecsJsonModalOpen",
    onExportJson: handleExportJson,
    onImport: handleImport,
  };

  return (
    <div
      className={`nodeSpecsModal ${!isSatDesignerSidebarOpen ? "left" : "right"}`}
    >
      <ThermalModalHeader header={header} />
      {!isModalMinimized && (
        <>
          <div className="nodeSpecsModal__inputs nodeSpecsModal__inputs--monaco">
            <MonacoEditor
              height="420px"
              language="json"
              theme="vs-dark"
              value={jsonInput}
              onChange={(value) => handleJsonChange(value ?? "")}
              onMount={(editor) => {
                editor.onDidBlurEditorWidget(() => handleJsonBlur(jsonInput, () => {}));
              }}
              options={{
                minimap: { enabled: false },
                fontSize: 12,
                fontFamily: "'Fira Code', 'Consolas', monospace",
                scrollBeyondLastLine: false,
                automaticLayout: true,
                wordWrap: "on",
                lineNumbers: "on",
                renderLineHighlight: "all",
                tabSize: 2,
                formatOnPaste: true,
                scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
              }}
            />
          </div>
          <div className="nodeSpecsModal__json-toggle" onClick={() => setShowAllNodes((v) => !v)}>
            <img
              src={
                showAllNodes
                  ? require("../../../../../../../assets/images/svgs/roundChecked.svg").default
                  : require("../../../../../../../assets/images/svgs/roundUnChecked.svg").default
              }
              height={16}
              width={16}
              alt={showAllNodes ? "checked" : "unchecked"}
            />
            <span>Show All Nodes</span>
          </div>
          <div className="nodeSpecsModal__divider__div">
            <DividerLine />
          </div>
          <ThermalModalFooter onSave={handleSaveAndExit} onSaveAndNext={handleSaveAndNext} isLastNode={isLastNode} onCancel={handleCancel} />
        </>
      )}

      {typeof document !== "undefined" &&
        createPortal(
          <ConfirmationDialog
            type="update"
            open={showConfirmDialog}
            title="Confirm Submission"
            description="Are you sure you want to submit the thermal configuration?"
            onConfirm={handleConfirmSave}
            onCancel={handleCancelConfirmation}
            confirm_button_text="Submit"
            cancel_button_text="Cancel"
          />,
          document.body
        )}
    </div>
  );
};

export default NodeSpecsJsonModal;
